import React, { useEffect, useState, useRef } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "../lib/api";
import { Loader2, ArrowLeft, Calendar, MapPin, CheckCircle2, AlertTriangle, ChevronRight, Sparkles } from "lucide-react";

const IngredientDetail = () => {
  const { slug } = useParams();
  const [ing, setIng] = useState(null);
  const [loading, setLoading] = useState(true);
  const [recipes, setRecipes] = useState([]);
  const pollRef = useRef(null);

  useEffect(() => {
    setLoading(true);
    api.get(`/ingredients/${slug}`)
      .then((r) => {
        setIng(r.data);
        // Fetch recipes that mention this ingredient
        return api.get(`/recipes?q=${encodeURIComponent(r.data.name)}&limit=6`);
      })
      .then((r) => setRecipes(r?.data || []))
      .catch(() => setIng(null))
      .finally(() => setLoading(false));
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [slug]);

  useEffect(() => {
    if (!ing || ing.image_status !== "pending") return;
    let attempts = 0;
    pollRef.current = setInterval(async () => {
      attempts += 1;
      try {
        const r = await api.get(`/ingredients/${slug}/image_status`);
        if (r.data.status === "ready" && r.data.image_data) {
          setIng((s) => ({ ...s, image_data: r.data.image_data, image_status: "ready" }));
          clearInterval(pollRef.current);
        } else if (r.data.status === "failed" || attempts > 30) {
          clearInterval(pollRef.current);
        }
      } catch { if (attempts > 30) clearInterval(pollRef.current); }
    }, 4000);
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [ing?.slug, ing?.image_status, slug]);

  if (loading) return <div className="min-h-screen flex items-center justify-center bg-stone-950"><Loader2 className="w-6 h-6 animate-spin text-orange-600" /></div>;
  if (!ing) return (
    <div className="min-h-screen flex items-center justify-center bg-stone-950 pt-24 px-6">
      <div className="text-center">
        <div className="magazine-number text-9xl mb-6">Ø</div>
        <p className="font-serif-display italic text-2xl text-stone-400 mb-8">Not in this larder.</p>
        <Link to="/ingredients" className="text-orange-500 uppercase text-xs tracking-[0.24em] ink-link">Back to the larder</Link>
      </div>
    </div>
  );
  const heroImg = ing.image_data ? `data:image/png;base64,${ing.image_data}` : null;

  return (
    <div className="min-h-screen pt-24 pb-32 bg-stone-950">
      <div className="max-w-[1200px] mx-auto px-6 sm:px-10">
        <Link to="/ingredients" data-testid="ing-back" className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.24em] text-stone-500 hover:text-orange-500 mb-10 ink-link">
          <ArrowLeft className="w-3.5 h-3.5" /> The larder
        </Link>
        <header className="grid lg:grid-cols-12 gap-10 lg:gap-16 mb-20">
          <div className="lg:col-span-6">
            <div className="eyebrow mb-6 text-orange-500">{ing.category}</div>
            <h1 data-testid="ing-title" className="font-serif-display text-6xl md:text-7xl text-stone-100 leading-[0.9] tracking-tighter">{ing.name}</h1>
            <p className="mt-6 font-serif-display italic text-2xl text-orange-500">{ing.tagline}</p>
            <p className="mt-8 text-stone-300 leading-relaxed text-lg">{ing.intro}</p>
            {ing.when_in_season?.length > 0 && (
              <div className="mt-6 flex items-center gap-3 text-sm text-stone-400 font-mono-accent">
                <Calendar className="w-4 h-4 text-orange-600" />
                In season: {ing.when_in_season.map((s) => <span key={s} className="capitalize mx-1">{s}</span>)}
              </div>
            )}
          </div>
          <div className="lg:col-span-6">
            {heroImg ? (
              <img src={heroImg} alt={ing.name} className="w-full aspect-[4/3] object-cover" />
            ) : (
              <div className="w-full aspect-[4/3] bg-gradient-to-br from-orange-950 via-stone-900 to-stone-950 border border-stone-800 flex flex-col items-center justify-center gap-3">
                {ing.image_status === "pending" && <Loader2 className="w-6 h-6 animate-spin text-orange-600/60" />}
                <span className="font-serif-display italic text-lg text-stone-400">Photograph on its way</span>
              </div>
            )}
          </div>
        </header>

        <div className="grid lg:grid-cols-2 gap-10 mb-16">
          {ing.where_to_find && (
            <section className="border-t border-stone-800 pt-6">
              <div className="flex items-center gap-3 mb-4"><MapPin className="w-4 h-4 text-orange-600" /><h2 className="eyebrow">Where to find it</h2></div>
              <p className="text-stone-300 leading-relaxed">{ing.where_to_find}</p>
            </section>
          )}
          {ing.pairs_with?.length > 0 && (
            <section className="border-t border-stone-800 pt-6">
              <div className="flex items-center gap-3 mb-4"><Sparkles className="w-4 h-4 text-orange-600" /><h2 className="eyebrow">Pairs with</h2></div>
              <div className="flex flex-wrap gap-2">
                {ing.pairs_with.map((p) => (
                  <span key={p} className="text-xs uppercase tracking-widest px-3 py-1.5 border border-stone-800 text-stone-300">{p}</span>
                ))}
              </div>
            </section>
          )}
        </div>

        <div className="grid lg:grid-cols-2 gap-10 mb-16">
          {ing.how_to_pick?.length > 0 && (
            <section className="border-t border-stone-800 pt-6">
              <div className="flex items-center gap-3 mb-4"><CheckCircle2 className="w-4 h-4 text-lime-500" /><h2 className="eyebrow">How to pick it</h2></div>
              <ul className="space-y-3">
                {ing.how_to_pick.map((h, i) => <li key={i} className="flex gap-3 text-stone-300"><ChevronRight className="w-3.5 h-3.5 mt-1.5 text-lime-500 shrink-0" /><span className="text-sm leading-relaxed">{h}</span></li>)}
              </ul>
            </section>
          )}
          {ing.how_to_prep?.length > 0 && (
            <section className="border-t border-stone-800 pt-6">
              <div className="flex items-center gap-3 mb-4"><CheckCircle2 className="w-4 h-4 text-orange-500" /><h2 className="eyebrow">How to treat it</h2></div>
              <ul className="space-y-3">
                {ing.how_to_prep.map((h, i) => <li key={i} className="flex gap-3 text-stone-300"><ChevronRight className="w-3.5 h-3.5 mt-1.5 text-orange-500 shrink-0" /><span className="text-sm leading-relaxed">{h}</span></li>)}
              </ul>
            </section>
          )}
        </div>

        {ing.watch_outs?.length > 0 && (
          <section className="border border-orange-900/40 bg-orange-950/10 p-6 mb-16">
            <div className="flex items-center gap-3 mb-4"><AlertTriangle className="w-4 h-4 text-orange-500" /><span className="eyebrow text-orange-500">Watch out</span></div>
            <ul className="space-y-2">
              {ing.watch_outs.map((w, i) => <li key={i} className="text-sm text-stone-300 leading-relaxed">&ndash; {w}</li>)}
            </ul>
          </section>
        )}

        {recipes.length > 0 && (
          <section>
            <div className="eyebrow mb-8 border-t border-stone-800 pt-8">Cook it: recipes with {ing.name.toLowerCase()}</div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {recipes.map((r) => (
                <Link key={r.id} to={`/recipe/${r.id}`} className="group border border-stone-800 bg-stone-900/40 p-5 hover:border-orange-600 transition-colors">
                  <div className="eyebrow mb-2 text-stone-500">{r.course} · {r.season}</div>
                  <h4 className="font-serif-display text-xl text-stone-100 group-hover:text-orange-500 transition-colors leading-tight">{r.title}</h4>
                </Link>
              ))}
            </div>
          </section>
        )}
      </div>
    </div>
  );
};

export default IngredientDetail;
